// frontend/src/services/employeeManagement.service.ts
// 🔥 COMPLETE FIXED VERSION - Proper Add, Delete, Comparison, Activity Logging
// ============================================================================

import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  deleteDoc, 
  updateDoc,
  getDoc,
  query, 
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  increment,
  writeBatch,
  limit as firestoreLimit,
  setDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { 
  EmployeeRecord, 
  NewEmployeeData, 
  BatchAddResult, 
  ResetRegistrationResult,
  ExcelEmployeeData,
  ImportResult,
  MasterSheetEmployee,
  EmployeeComparisonResult,
  AdminActivity
} from '../types/employeeManagement.types';

export interface DeleteEmployeeOptions {
  employeeId: string;
  reason: string;
  deletedBy: string;
  deletedByName?: string;
  deletedByEmployeeNo?: string;
  hardDelete?: boolean;
}

export interface AdminInfo {
  email: string;
  name?: string;
  employeeNo?: string;
}

class EmployeeManagementService {
  private collectionName = 'employees';
  private deletedEmployeesCollection = 'deleted_employees';
  private uuidHistoryCollection = 'employee_uuid_history';
  private activityLogCollection = 'admin_activity_log';
  private permanentDeletionsCollection = 'permanent_deletions';
  private masterSheetPath = 'MasterSheet/Employee-Data/employees';

  // ========================================================================
  // 🆕 ACTIVITY LOGGING METHODS
  // ========================================================================

  /**
   * ✅ Log admin activity with full details
   */
  private async logActivity(activity: {
    action: 'add' | 'delete' | 'reset' | 'update' | 'bulk_add' | 'import' | 'restore' | 'permanent_delete';
    targetEmployeeId?: string;
    targetEmployeeName?: string;
    targetEmployeePin?: string;
    performedBy: string;
    performedByName?: string;
    performedByEmployeeNo?: string;
    details: string;
    metadata?: any;
  }): Promise<void> {
    try {
      await addDoc(collection(db, this.activityLogCollection), {
        ...activity,
        timestamp: serverTimestamp(),
        createdAt: new Date().toISOString()
      });
      
      console.log(`📝 Activity logged: ${activity.action} by ${activity.performedByName || activity.performedBy}`);
    } catch (error) {
      console.error('Failed to log activity:', error);
    }
  }

  /**
   * ✅ Get activity logs (for admin dashboard)
   */
  async getActivityLogs(maxResults: number = 50): Promise<AdminActivity[]> {
    try {
      const q = query(
        collection(db, this.activityLogCollection),
        orderBy('timestamp', 'desc'),
        firestoreLimit(maxResults)
      );
      
      const snapshot = await getDocs(q);
      const activities: AdminActivity[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        activities.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate?.() || data.timestamp
        } as AdminActivity);
      });
      
      return activities;
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      return [];
    }
  }

  /**
   * ✅ Get activity logs for specific employee
   */
  async getEmployeeActivityLogs(employeeId: string): Promise<AdminActivity[]> {
    try {
      const q = query(
        collection(db, this.activityLogCollection),
        where('targetEmployeeId', '==', employeeId),
        orderBy('timestamp', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const activities: AdminActivity[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        activities.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate?.() || data.timestamp
        } as AdminActivity);
      });
      
      return activities;
    } catch (error) {
      console.error('Error fetching employee activity logs:', error);
      return [];
    }
  }

  // ========================================================================
  // 🔥 UUID PRESERVATION METHODS
  // ========================================================================

  /**
   * ✅ Find existing UUID for a PIN across all collections
   */
  async getAllUUIDsForPin(pin: string): Promise<string[]> {
    try {
      const uuids = new Set<string>();
      
      // Get from active employees (but NOT deleted ones in main collection)
      const activeQuery = query(
        collection(db, this.collectionName),
        where('pin', '==', pin)
      );
      const activeSnapshot = await getDocs(activeQuery);
      activeSnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Only add if it's a soft-deleted employee (for reuse)
        if (data.isDeleted === true) {
          uuids.add(doc.id);
        }
      });
      
      // Get from deleted employees backup
      const deletedQuery = query(
        collection(db, this.deletedEmployeesCollection),
        where('pin', '==', pin)
      );
      const deletedSnapshot = await getDocs(deletedQuery);
      deletedSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.originalUUID) uuids.add(data.originalUUID);
      });
      
      // Get from UUID history
      const historyQuery = query(
        collection(db, this.uuidHistoryCollection),
        where('pin', '==', pin)
      );
      const historySnapshot = await getDocs(historyQuery);
      historySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.uuid) uuids.add(data.uuid);
      });
      
      const uuidArray = Array.from(uuids);
      if (uuidArray.length > 0) {
        console.log(`📋 Found ${uuidArray.length} UUID(s) for PIN ${pin}:`, uuidArray);
      }
      
      return uuidArray;
      
    } catch (error) {
      console.error('Error getting all UUIDs for PIN:', error);
      return [];
    }
  }

  // ========================================================================
  // 🔥 ENHANCED DELETE METHODS - SOFT + PERMANENT
  // ========================================================================

  /**
   * ✅ SOFT DELETE - Preserves UUID and all data
   */
  async deleteEmployeeSoft(options: DeleteEmployeeOptions): Promise<{
    success: boolean;
    message: string;
  }> {
    const { 
      employeeId, 
      reason, 
      deletedBy, 
      deletedByName,
      deletedByEmployeeNo 
    } = options;
    
    try {
      const employeeRef = doc(db, this.collectionName, employeeId);
      const employeeDoc = await getDoc(employeeRef);
      
      if (!employeeDoc.exists()) {
        return { success: false, message: 'Employee not found' };
      }
      
      const employeeData = employeeDoc.data();
      const batch = writeBatch(db);
      
      // 1. Mark as deleted in main collection (keep the document!)
      batch.update(employeeRef, {
        isDeleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: deletedBy,
        deletedByName: deletedByName || 'Admin',
        deletedByEmployeeNo: deletedByEmployeeNo || '',
        deletionReason: reason,
        deletionType: 'soft',
        lastUpdated: serverTimestamp()
      });
      
      // 2. Create backup in deleted_employees
      const deletedRef = doc(collection(db, this.deletedEmployeesCollection));
      batch.set(deletedRef, {
        ...employeeData,
        originalUUID: employeeId,
        isDeleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: deletedBy,
        deletedByName: deletedByName || 'Admin',
        deletedByEmployeeNo: deletedByEmployeeNo || '',
        deletionReason: reason,
        deletionType: 'soft',
        backupCreatedAt: serverTimestamp()
      });
      
      // 3. Log to UUID history
      const historyRef = doc(collection(db, this.uuidHistoryCollection));
      batch.set(historyRef, {
        uuid: employeeId,
        pin: employeeData.pin,
        name: employeeData.name,
        action: 'soft_deleted',
        timestamp: serverTimestamp(),
        performedBy: deletedBy,
        performedByName: deletedByName || 'Admin',
        performedByEmployeeNo: deletedByEmployeeNo || '',
        reason: reason
      });
      
      await batch.commit();
      
      // 4. Log admin activity
      await this.logActivity({
        action: 'delete',
        targetEmployeeId: employeeId,
        targetEmployeeName: employeeData.name,
        targetEmployeePin: employeeData.pin,
        performedBy: deletedBy,
        performedByName: deletedByName,
        performedByEmployeeNo: deletedByEmployeeNo,
        details: `Soft deleted employee: ${employeeData.name} (PIN: ${employeeData.pin}). Reason: ${reason}`,
        metadata: {
          deletionType: 'soft',
          uuidPreserved: true,
          reason: reason
        }
      });
      
      return {
        success: true,
        message: `Employee soft deleted. UUID ${employeeId} preserved for future re-registration.`
      };
      
    } catch (error) {
      console.error('Error soft deleting employee:', error);
      return {
        success: false,
        message: `Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * 🔥 PERMANENT DELETE - Removes all traces (for employees who left company)
   */
  async deleteEmployeePermanent(options: DeleteEmployeeOptions): Promise<{
    success: boolean;
    message: string;
  }> {
    const { 
      employeeId, 
      reason, 
      deletedBy,
      deletedByName,
      deletedByEmployeeNo
    } = options;
    
    try {
      const employeeRef = doc(db, this.collectionName, employeeId);
      const employeeDoc = await getDoc(employeeRef);
      
      if (!employeeDoc.exists()) {
        return { success: false, message: 'Employee not found' };
      }
      
      const employeeData = employeeDoc.data();
      const batch = writeBatch(db);
      
      // 1. Create permanent deletion record (for audit trail only)
      const permanentDeletionRef = doc(collection(db, this.permanentDeletionsCollection));
      batch.set(permanentDeletionRef, {
        originalEmployeeData: {
          pin: employeeData.pin,
          name: employeeData.name,
          designation: employeeData.designation,
          department: employeeData.department || 'N/A',
          email: employeeData.email || 'N/A',
          employeeNumber: employeeData.employeeNumber || 'N/A'
        },
        originalUUID: employeeId,
        deletedAt: serverTimestamp(),
        deletedBy: deletedBy,
        deletedByName: deletedByName || 'Admin',
        deletedByEmployeeNo: deletedByEmployeeNo || '',
        deletionReason: reason,
        deletionType: 'permanent',
        warning: 'This employee was permanently deleted and cannot be restored'
      });
      
      // 2. Log final UUID history entry
      const historyRef = doc(collection(db, this.uuidHistoryCollection));
      batch.set(historyRef, {
        uuid: employeeId,
        pin: employeeData.pin,
        name: employeeData.name,
        action: 'permanent_deleted',
        timestamp: serverTimestamp(),
        performedBy: deletedBy,
        performedByName: deletedByName || 'Admin',
        performedByEmployeeNo: deletedByEmployeeNo || '',
        reason: reason,
        warning: 'UUID permanently removed - cannot be reused'
      });
      
      // 3. Delete from main collection
      batch.delete(employeeRef);
      
      await batch.commit();
      
      // 4. Delete from soft deleted backups if exists (outside batch)
      try {
        const deletedQuery = query(
          collection(db, this.deletedEmployeesCollection),
          where('originalUUID', '==', employeeId)
        );
        const deletedSnapshot = await getDocs(deletedQuery);
        for (const docSnap of deletedSnapshot.docs) {
          await deleteDoc(docSnap.ref);
        }
      } catch (cleanupError) {
        console.warn('Cleanup of backup failed:', cleanupError);
      }
      
      // 5. Log admin activity
      await this.logActivity({
        action: 'permanent_delete',
        targetEmployeeId: employeeId,
        targetEmployeeName: employeeData.name,
        targetEmployeePin: employeeData.pin,
        performedBy: deletedBy,
        performedByName: deletedByName,
        performedByEmployeeNo: deletedByEmployeeNo,
        details: `⚠️ PERMANENTLY DELETED employee: ${employeeData.name} (PIN: ${employeeData.pin}). Reason: ${reason}. UUID destroyed - CANNOT BE RESTORED.`,
        metadata: {
          deletionType: 'permanent',
          uuidDestroyed: true,
          reason: reason,
          warning: 'Cannot be restored'
        }
      });
      
      return {
        success: true,
        message: `Employee permanently deleted. All data including UUID has been removed. This action CANNOT be undone.`
      };
      
    } catch (error) {
      console.error('Error permanently deleting employee:', error);
      return {
        success: false,
        message: `Failed to permanently delete: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  // ========================================================================
  // 🔥 FIXED ADD EMPLOYEE - WITH UUID REUSE + ACTIVITY LOGGING
  // ========================================================================

  async addEmployee(
    employeeData: NewEmployeeData, 
    adminInfo?: AdminInfo
  ): Promise<string> {
    try {
      console.log('📝 Adding employee:', employeeData.name, 'PIN:', employeeData.pin);
      
      // 1. Check if employee already exists (ACTIVE employees only)
      const existsResult = await this.checkEmployeeExists(
        employeeData.pin, 
        employeeData.name, 
        employeeData.employeeNumber
      );
  
      if (existsResult.exists) {
        throw new Error(`Employee already exists with ${existsResult.existingField}: ${existsResult.existingEmployee?.name || 'Unknown'}`);
      }

      // 2. 🔥 CHECK FOR EXISTING UUID (for re-registration of previously deleted)
      const existingUUIDs = await this.getAllUUIDsForPin(employeeData.pin);
      let employeeId: string;

      const employee: Omit<EmployeeRecord, 'id'> = {
        pin: employeeData.pin.trim(),
        name: employeeData.name.trim(),
        designation: employeeData.designation.trim(),
        registrationCompleted: false,
        profileCompleted: false,
        faceRegistered: false,
        registrationResetCount: 0,
        isDeleted: false, // ✅ CRITICAL - Always set to false for new/restored
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        registeredOn: null
      };
  
      // Add optional fields
      if (employeeData.employeeNumber) employee.employeeNumber = employeeData.employeeNumber.trim();
      if (employeeData.department) employee.department = employeeData.department.trim();
      if (employeeData.email) employee.email = employeeData.email.trim();
      if (employeeData.phone) employee.phone = employeeData.phone.trim();
      if (employeeData.country) employee.country = employeeData.country.trim();

      // 3. 🔥 REUSE EXISTING UUID OR CREATE NEW
      if (existingUUIDs.length > 0) {
        // REUSE FIRST UUID FOUND (restore deleted employee)
        employeeId = existingUUIDs[0];
        const employeeRef = doc(db, this.collectionName, employeeId);
        
        // Check if the document exists and needs to be restored
        const existingDoc = await getDoc(employeeRef);
        
        if (existingDoc.exists()) {
          // Update existing soft-deleted document
          await updateDoc(employeeRef, {
            ...employee,
            isDeleted: false, // ✅ RESTORE
            restoredFromUUID: true,
            previousUUID: employeeId,
            restoredAt: serverTimestamp(),
            // Clear old deletion info
            deletedAt: null,
            deletedBy: null,
            deletedByName: null,
            deletionReason: null,
            deletionType: null
          });
        } else {
          // Create new document with old UUID
          await setDoc(employeeRef, {
            ...employee,
            restoredFromUUID: true,
            previousUUID: employeeId,
            restoredAt: serverTimestamp()
          });
        }
        
        console.log(`♻️ Reused existing UUID ${employeeId} for PIN ${employeeData.pin}`);
        
        // Log UUID reuse to history
        await addDoc(collection(db, this.uuidHistoryCollection), {
          uuid: employeeId,
          pin: employeeData.pin,
          name: employeeData.name,
          action: 'uuid_reused',
          timestamp: serverTimestamp(),
          performedBy: adminInfo?.email || 'system',
          performedByName: adminInfo?.name || 'System',
          performedByEmployeeNo: adminInfo?.employeeNo || ''
        });
        
      } else {
        // CREATE NEW EMPLOYEE WITH NEW UUID
        const docRef = await addDoc(collection(db, this.collectionName), employee);
        employeeId = docRef.id;
        console.log(`✅ Created new employee with UUID: ${employeeId}`);
        
        // Log new UUID creation
        await addDoc(collection(db, this.uuidHistoryCollection), {
          uuid: employeeId,
          pin: employeeData.pin,
          name: employeeData.name,
          action: 'created',
          timestamp: serverTimestamp(),
          performedBy: adminInfo?.email || 'system',
          performedByName: adminInfo?.name || 'System',
          performedByEmployeeNo: adminInfo?.employeeNo || ''
        });
      }

      // 4. Log admin activity
      await this.logActivity({
        action: 'add',
        targetEmployeeId: employeeId,
        targetEmployeeName: employeeData.name,
        targetEmployeePin: employeeData.pin,
        performedBy: adminInfo?.email || 'system',
        performedByName: adminInfo?.name || 'System',
        performedByEmployeeNo: adminInfo?.employeeNo || '',
        details: `Added employee: ${employeeData.name} (PIN: ${employeeData.pin})${existingUUIDs.length > 0 ? ' - Reused existing UUID (restored)' : ' - New UUID created'}`,
        metadata: {
          uuidReused: existingUUIDs.length > 0,
          originalUUID: existingUUIDs[0] || null,
          designation: employeeData.designation,
          department: employeeData.department || 'N/A'
        }
      });
      
      return employeeId;
      
    } catch (error) {
      console.error('❌ Error adding employee:', error);
      throw error;
    }
  }

  // ========================================================================
  // 🔥 ENHANCED RESET REGISTRATION - WITH ACTIVITY LOGGING
  // ========================================================================

  async resetEmployeeRegistration(
    employeeId: string,
    adminInfo?: AdminInfo
  ): Promise<ResetRegistrationResult> {
    try {
      const employeeRef = doc(db, this.collectionName, employeeId);
      const employeeDoc = await getDoc(employeeRef);
      
      if (!employeeDoc.exists()) {
        throw new Error('Employee not found');
      }

      const employeeData = employeeDoc.data();
      const currentResetCount = employeeData.registrationResetCount || 0;

      await updateDoc(employeeRef, {
        registrationCompleted: false,
        profileCompleted: false,
        faceRegistered: false,
        registeredOn: null,
        registrationTimestamp: null,
        image: null, // 🔥 Clear face image
        faceFeatures: null, // 🔥 Clear face features
        enhancedFaceFeatures: null, // 🔥 Clear enhanced features
        lastRegistrationReset: serverTimestamp(),
        lastResetBy: adminInfo?.email || 'admin',
        lastResetByName: adminInfo?.name || 'Admin',
        lastResetByEmployeeNo: adminInfo?.employeeNo || '',
        registrationResetCount: increment(1),
        lastUpdated: serverTimestamp()
      });

      // Log admin activity
      await this.logActivity({
        action: 'reset',
        targetEmployeeId: employeeId,
        targetEmployeeName: employeeData.name,
        targetEmployeePin: employeeData.pin,
        performedBy: adminInfo?.email || 'admin',
        performedByName: adminInfo?.name || 'Admin',
        performedByEmployeeNo: adminInfo?.employeeNo || '',
        details: `Reset registration for: ${employeeData.name} (PIN: ${employeeData.pin}). Reset count: ${currentResetCount + 1}`,
        metadata: {
          newResetCount: currentResetCount + 1,
          previousResets: currentResetCount
        }
      });

      const updatedDoc = await getDoc(employeeRef);
      const updatedData = updatedDoc.data();
      
      return {
        success: true,
        message: `Registration reset successfully. Employee can now register again. Total resets: ${currentResetCount + 1}`,
        employee: { id: employeeId, ...updatedData } as EmployeeRecord
      };

    } catch (error) {
      return { 
        success: false, 
        message: `Failed to reset: ${error instanceof Error ? error.message : 'Unknown error'}` 
      };
    }
  }

  // ========================================================================
  // 🔥 FIXED GET ALL EMPLOYEES - EXCLUDES DELETED
  // ========================================================================

  async getAllEmployees(): Promise<EmployeeRecord[]> {
    try {
      // Simple query without complex filtering (to avoid index issues)
      const q = query(
        collection(db, this.collectionName),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const employees: EmployeeRecord[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // 🔥 FILTER OUT DELETED EMPLOYEES IN CODE
        if (data.isDeleted === true) {
          return; // Skip deleted employees
        }
        
        employees.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || data.createdAt,
          lastUpdated: data.lastUpdated?.toDate?.() || data.lastUpdated,
          registeredOn: data.registeredOn?.toDate?.() || data.registeredOn,
          registrationTimestamp: data.registrationTimestamp?.toDate?.() || data.registrationTimestamp,
          lastLoginDate: data.lastLoginDate?.toDate?.() || data.lastLoginDate,
          lastRegistrationReset: data.lastRegistrationReset?.toDate?.() || data.lastRegistrationReset,
        } as EmployeeRecord);
      });
      
      console.log(`✅ Loaded ${employees.length} active employees`);
      return employees;
      
    } catch (error) {
      console.error('Error fetching employees:', error);
      throw new Error('Failed to fetch employees');
    }
  }

  // ========================================================================
  // 🔥 FIXED EMPLOYEE COMPARISON - PROPER MATCHING LOGIC
  // ========================================================================

  async getEmployeeComparison(): Promise<EmployeeComparisonResult> {
    try {
      console.log('📊 Starting comprehensive employee comparison...');
      
      const masterSheetEmployees = await this.getMasterSheetEmployees();
      console.log(`📋 MasterSheet employees: ${masterSheetEmployees.length}`);
      
      const appRegistrationEmployees = await this.getAllEmployees();
      console.log(`📱 App registration employees: ${appRegistrationEmployees.length}`);
      
      // 🔥 FIXED: Create proper lookup maps
      const appEmployeesByPin = new Map<string, EmployeeRecord>();
      const appEmployeesByName = new Map<string, EmployeeRecord>();
      
      appRegistrationEmployees.forEach(emp => {
        if (emp.pin) {
          // Normalize PIN (remove leading zeros, lowercase)
          const normalizedPin = emp.pin.toString().toLowerCase().trim().replace(/^0+/, '');
          appEmployeesByPin.set(normalizedPin, emp);
          appEmployeesByPin.set(emp.pin.toLowerCase().trim(), emp); // Also store original
        }
        if (emp.name) {
          appEmployeesByName.set(emp.name.toLowerCase().trim(), emp);
        }
      });
      
      console.log(`📋 App employees indexed: ${appEmployeesByPin.size} by PIN, ${appEmployeesByName.size} by name`);
      
      // 🔥 FIXED: Proper comparison
      const needsAppRegistration: MasterSheetEmployee[] = [];
      const registeredEmployees: EmployeeRecord[] = [];
      const processedMasterSheet = new Set<string>();
      
      masterSheetEmployees.forEach(masterEmp => {
        const pin = this.extractPin(masterEmp);
        const name = (masterEmp.employeeName || masterEmp.name || '').toLowerCase().trim();
        const uniqueKey = `${pin}-${name}`;
        
        // Skip duplicates
        if (processedMasterSheet.has(uniqueKey)) return;
        processedMasterSheet.add(uniqueKey);
        
        // 🔥 CHECK IF EXISTS IN APP REGISTRATION
        let matchedAppEmployee: EmployeeRecord | undefined = undefined;
        
        // Check by PIN first
        if (pin) {
          const normalizedPin = pin.toString().toLowerCase().trim().replace(/^0+/, '');
          matchedAppEmployee = appEmployeesByPin.get(normalizedPin) || appEmployeesByPin.get(pin.toLowerCase().trim());
        }
        
        // If not found by PIN, check by name
        if (!matchedAppEmployee && name) {
          matchedAppEmployee = appEmployeesByName.get(name);
        }
        
        if (matchedAppEmployee) {
          // Employee EXISTS in app registration
          registeredEmployees.push(matchedAppEmployee);
        } else {
          // Employee NOT in app registration
          needsAppRegistration.push(masterEmp);
        }
      });
      
      // Calculate stats
      const fullyRegistered = appRegistrationEmployees.filter(emp => 
        this.isActuallyFaceRegistered(emp)
      );
      const pendingRegistration = appRegistrationEmployees.filter(emp => 
        !this.isActuallyFaceRegistered(emp)
      );
      
      const registrationStats = this.calculateRegistrationStats(appRegistrationEmployees);
      
      const result: EmployeeComparisonResult = {
        totalCompanyEmployees: masterSheetEmployees.length,
        totalAppRegistered: appRegistrationEmployees.length,
        totalCompletelyRegistered: fullyRegistered.length,
        totalPendingRegistration: pendingRegistration.length,
        totalNeedsAppRegistration: needsAppRegistration.length,
        masterSheetEmployees,
        appRegistrationEmployees,
        needsAppRegistration,
        registeredEmployees: Array.from(new Set(registeredEmployees.map(emp => emp.id))).map(id => 
          registeredEmployees.find(emp => emp.id === id)!
        ).filter(Boolean),
        registrationStats,
        summary: {
          companyTotal: masterSheetEmployees.length,
          addedForApp: appRegistrationEmployees.length,
          fullyRegistered: fullyRegistered.length,
          pendingSetup: pendingRegistration.length,
          notAddedYet: needsAppRegistration.length,
          percentage: {
            addedForApp: masterSheetEmployees.length > 0 
              ? Math.round((appRegistrationEmployees.length / masterSheetEmployees.length) * 100) 
              : 0,
            fullyRegistered: masterSheetEmployees.length > 0 
              ? Math.round((fullyRegistered.length / masterSheetEmployees.length) * 100) 
              : 0
          }
        }
      };
      
      console.log('✅ Comparison result:', {
        totalCompany: result.totalCompanyEmployees,
        appRegistered: result.totalAppRegistered,
        needsAdding: result.totalNeedsAppRegistration,
        fullyRegistered: result.totalCompletelyRegistered
      });
      
      return result;
      
    } catch (error) {
      console.error('❌ Error in employee comparison:', error);
      throw new Error('Failed to compare employee data');
    }
  }

  private isActuallyFaceRegistered(data: any): boolean {
    if (data.faceRegistered === true && data.profileCompleted === true) return true;
    if (data.registrationCompleted !== undefined) {
      if (data.registrationCompleted === true) return true;
      if (data.registrationCompleted === false) return false;
    }
    if (data.image && typeof data.image === 'string' && data.image.length > 100) return true;
    if (data.enhancedFaceFeatures && typeof data.enhancedFaceFeatures === 'object' && 
        Object.keys(data.enhancedFaceFeatures).length > 0) return true;
    if (data.faceFeatures && typeof data.faceFeatures === 'object' && 
        Object.keys(data.faceFeatures).length > 0) return true;
    return false;
  }

  async getMasterSheetEmployees(): Promise<MasterSheetEmployee[]> {
    try {
      const masterSheetRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');
      const snapshot = await getDocs(masterSheetRef);
      
      const employees: MasterSheetEmployee[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        employees.push({
          id: doc.id,
          employeeName: data.employeeName || data.name,
          name: data.name || data.employeeName,
          employeeNumber: data.employeeNumber || doc.id,
          designation: data.designation || 'Not Specified',
          department: data.department || 'Not Specified',
          email: data.email || '',
          phone: data.phone || data.mobileNo || '',
          pin: this.extractPin(data) || doc.id.replace(/^EMP/i, ''),
          salary: data.salary || 0,
          joinDate: data.joinDate || data.hiringDate || '',
          createdAt: data.createdAt?.toDate?.() || data.createdAt,
          ...data
        });
      });
      
      return employees;
    } catch (error) {
      console.error('❌ Error fetching MasterSheet employees:', error);
      throw new Error('Failed to fetch company employee data');
    }
  }

  private extractPin(data: any): string | null {
    const pinFields = ['pin', 'PIN', 'Pin', 'employeeNumber', 'empNo', 'Employee ID'];
    for (const field of pinFields) {
      if (data[field]) {
        const pin = String(data[field]).replace(/^EMP/i, '').trim();
        if (pin && pin.length >= 1) return pin;
      }
    }
    if (data.id) return data.id.replace(/^EMP/i, '');
    return null;
  }

  async bulkAddFromMasterSheet(
    employeeIds: string[],
    adminInfo?: AdminInfo
  ): Promise<BatchAddResult> {
    const result: BatchAddResult = { success: 0, errors: 0, skipped: 0, details: [] };

    try {
      const masterSheetEmployees = await this.getMasterSheetEmployees();
      const selectedEmployees = masterSheetEmployees.filter(emp => employeeIds.includes(emp.id));

      for (const masterEmp of selectedEmployees) {
        try {
          const pin = this.extractPin(masterEmp);
          const name = masterEmp.employeeName || masterEmp.name;
          
          if (!pin || !name || !masterEmp.designation) {
            result.errors++;
            result.details.push(`❌ ${name || 'Unknown'}: Missing required fields`);
            continue;
          }

          const existsResult = await this.checkEmployeeExists(pin, name, masterEmp.employeeNumber);
          
          if (existsResult.exists) {
            result.skipped++;
            result.details.push(`⏭️ ${name}: Already exists`);
            continue;
          }

          await this.addEmployee({
            pin, 
            name, 
            designation: masterEmp.designation,
            employeeNumber: masterEmp.employeeNumber,
            department: masterEmp.department,
            email: masterEmp.email,
            phone: masterEmp.phone
          }, adminInfo);
          
          result.success++;
          result.details.push(`✅ ${name}: Successfully added`);

        } catch (error) {
          result.errors++;
          result.details.push(`❌ ${masterEmp.name || 'Unknown'}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      // Log bulk add activity
      if (adminInfo) {
        await this.logActivity({
          action: 'bulk_add',
          performedBy: adminInfo.email,
          performedByName: adminInfo.name,
          performedByEmployeeNo: adminInfo.employeeNo,
          details: `Bulk added ${result.success} employees from MasterSheet. Success: ${result.success}, Skipped: ${result.skipped}, Errors: ${result.errors}`,
          metadata: {
            totalAttempted: employeeIds.length,
            success: result.success,
            skipped: result.skipped,
            errors: result.errors
          }
        });
      }

      return result;
    } catch (error) {
      return {
        success: 0, errors: 1, skipped: 0,
        details: [`❌ Bulk add failed: ${error instanceof Error ? error.message : 'Unknown error'}`]
      };
    }
  }

  private calculateRegistrationStats(employees: EmployeeRecord[]) {
    const periods = [1, 3, 7, 30];
    const stats = {
      total: employees.length,
      registered: employees.filter(emp => emp.registrationCompleted).length,
      pending: employees.filter(emp => !emp.registrationCompleted).length,
      recentlyRegistered: {} as Record<string, number>,
      totalResets: employees.reduce((sum, emp) => sum + (emp.registrationResetCount || 0), 0)
    };

    periods.forEach(days => {
      const xDaysAgo = new Date();
      xDaysAgo.setDate(xDaysAgo.getDate() - days);
      
      const count = employees.filter(emp => {
        if (!emp.registrationCompleted) return false;
        let registrationDate: Date | null = null;
        if (emp.registeredOn) registrationDate = emp.registeredOn instanceof Date ? emp.registeredOn : new Date(emp.registeredOn);
        else if (emp.registrationTimestamp) registrationDate = emp.registrationTimestamp instanceof Date ? emp.registrationTimestamp : new Date(emp.registrationTimestamp);
        else if (emp.createdAt) registrationDate = emp.createdAt instanceof Date ? emp.createdAt : new Date(emp.createdAt);
        return registrationDate && registrationDate >= xDaysAgo;
      }).length;
      
      stats.recentlyRegistered[`last${days}Days`] = count;
    });

    return stats;
  }

  async checkEmployeeExists(pin: string, name: string, employeeNumber?: string): Promise<{
    exists: boolean;
    existingField?: string;
    existingEmployee?: EmployeeRecord;
  }> {
    try {
      // Check by PIN (only active employees)
      const pinQuery = query(
        collection(db, this.collectionName), 
        where('pin', '==', pin)
      );
      const pinSnapshot = await getDocs(pinQuery);
      
      for (const doc of pinSnapshot.docs) {
        const data = doc.data();
        // Skip deleted employees
        if (data.isDeleted === true) continue;
        
        return { 
          exists: true, 
          existingField: 'PIN', 
          existingEmployee: { id: doc.id, ...data } as EmployeeRecord 
        };
      }

      // Check by name (only active employees)
      const allQuery = query(collection(db, this.collectionName));
      const allSnapshot = await getDocs(allQuery);
      
      for (const doc of allSnapshot.docs) {
        const data = doc.data();
        // Skip deleted employees
        if (data.isDeleted === true) continue;
        
        if (data.name && data.name.toLowerCase().trim() === name.toLowerCase().trim()) {
          return { 
            exists: true, 
            existingField: 'Name', 
            existingEmployee: { id: doc.id, ...data } as EmployeeRecord 
          };
        }
      }

      // Check by employee number if provided (only active employees)
      if (employeeNumber) {
        for (const doc of allSnapshot.docs) {
          const data = doc.data();
          if (data.isDeleted === true) continue;
          
          if (data.employeeNumber === employeeNumber) {
            return { 
              exists: true, 
              existingField: 'Employee Number', 
              existingEmployee: { id: doc.id, ...data } as EmployeeRecord 
            };
          }
        }
      }

      return { exists: false };
    } catch (error) {
      console.error('Error checking employee exists:', error);
      return { exists: false };
    }
  }

  async importEmployeesFromExcel(
    excelData: ExcelEmployeeData[],
    adminInfo?: AdminInfo
  ): Promise<ImportResult> {
    const result: ImportResult = { 
      totalProcessed: excelData.length, 
      successful: 0, 
      errors: 0, 
      skipped: 0, 
      details: [] 
    };

    for (let i = 0; i < excelData.length; i++) {
      const row = excelData[i];
      try {
        if (!row.PIN || !row.Name || !row.Designation) {
          result.errors++;
          result.details.push(`❌ Row ${i + 1}: Missing required fields (PIN, Name, or Designation)`);
          continue;
        }

        const employeeData: NewEmployeeData = {
          pin: String(row.PIN).trim(),
          name: String(row.Name).trim(),
          designation: String(row.Designation).trim(),
          department: row.Department ? String(row.Department).trim() : undefined,
          email: row.Email ? String(row.Email).trim() : undefined,
          phone: row.Phone ? String(row.Phone).trim() : undefined,
          country: row.Country ? String(row.Country).trim() : undefined,
          employeeNumber: row['Employee Number'] ? String(row['Employee Number']).trim() : undefined
        };

        const existsResult = await this.checkEmployeeExists(
          employeeData.pin, 
          employeeData.name, 
          employeeData.employeeNumber
        );

        if (existsResult.exists) {
          result.skipped++;
          result.details.push(`⏭️ Row ${i + 1}: ${employeeData.name} already exists`);
          continue;
        }

        await this.addEmployee(employeeData, adminInfo);
        result.successful++;
        result.details.push(`✅ Row ${i + 1}: Successfully added ${employeeData.name}`);

      } catch (error) {
        result.errors++;
        result.details.push(`❌ Row ${i + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    // Log import activity
    if (adminInfo) {
      await this.logActivity({
        action: 'import',
        performedBy: adminInfo.email,
        performedByName: adminInfo.name,
        performedByEmployeeNo: adminInfo.employeeNo,
        details: `Excel import completed. Success: ${result.successful}, Skipped: ${result.skipped}, Errors: ${result.errors}`,
        metadata: {
          totalProcessed: result.totalProcessed,
          successful: result.successful,
          skipped: result.skipped,
          errors: result.errors
        }
      });
    }

    return result;
  }

  // Legacy delete method (hard delete - kept for backward compatibility)
  async deleteEmployee(employeeId: string): Promise<void> {
    await deleteDoc(doc(db, this.collectionName, employeeId));
  }

  async getEmployeeById(employeeId: string): Promise<EmployeeRecord | null> {
    try {
      const employeeRef = doc(db, this.collectionName, employeeId);
      const employeeDoc = await getDoc(employeeRef);
      
      if (!employeeDoc.exists()) return null;

      const data = employeeDoc.data();
      return {
        id: employeeId,
        ...data,
        createdAt: data.createdAt?.toDate?.() || data.createdAt,
        lastUpdated: data.lastUpdated?.toDate?.() || data.lastUpdated,
        registeredOn: data.registeredOn?.toDate?.() || data.registeredOn,
        registrationTimestamp: data.registrationTimestamp?.toDate?.() || data.registrationTimestamp,
        lastLoginDate: data.lastLoginDate?.toDate?.() || data.lastLoginDate,
        lastRegistrationReset: data.lastRegistrationReset?.toDate?.() || data.lastRegistrationReset,
      } as EmployeeRecord;

    } catch (error) {
      throw new Error('Failed to get employee');
    }
  }

  async getRegistrationStats() {
    const employees = await this.getAllEmployees();
    const stats = {
      total: employees.length,
      registered: employees.filter(emp => emp.registrationCompleted).length,
      pending: employees.filter(emp => !emp.registrationCompleted).length,
      recentlyRegistered: 0,
      totalResets: employees.reduce((sum, emp) => sum + (emp.registrationResetCount || 0), 0)
    };

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    stats.recentlyRegistered = employees.filter(emp => {
      if (!emp.registrationCompleted || !emp.registeredOn) return false;
      const regDate = emp.registeredOn instanceof Date ? emp.registeredOn : new Date(emp.registeredOn);
      return regDate >= sevenDaysAgo;
    }).length;

    return stats;
  }

  validateEmployeeData(data: NewEmployeeData): string[] {
    const errors: string[] = [];
    if (!data.name?.trim()) errors.push('Name is required');
    if (!data.pin?.trim()) errors.push('PIN is required');
    if (!data.designation?.trim()) errors.push('Designation is required');
    
    if (data.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) errors.push('Invalid email format');
    }

    if (data.pin && (data.pin.length < 1 || data.pin.length > 10)) {
      errors.push('PIN must be between 1 and 10 characters');
    }

    return errors;
  }
}

export const employeeManagementService = new EmployeeManagementService();